import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiPost } from '../../api/client';

const fmt = (n: number) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// Delete = reversal (permission materialPurchase:delete): the purchase stays on record marked reversed; the server re-checks everything.
export function DeleteMaterialPurchaseDialog({ purchase, onClose }: { purchase: any | null; onClose: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>();
  const open = !!purchase;
  useEffect(() => { setReason(''); setError(undefined); }, [purchase?.id]);
  const mutation = useMutation({
    mutationFn: () => apiPost<any>(`/sales/material-purchases/${purchase.id}/delete`, { reason: reason.trim() }),
    onSuccess: () => {
      for (const key of ['material-purchases', 'client-credit', 'clients', 'old-gold-exchanges', 'metal-stock']) qc.invalidateQueries({ queryKey: [key] });
      enqueueSnackbar(`Material purchase ${purchase.exchangeNumber} deleted (reversed)`, { variant: 'success' });
      onClose();
    },
    onError: (e: any) => setError(e.response?.data?.error ?? 'Could not delete the material purchase'),
  });
  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete material purchase</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Delete <b style={{ fontFamily: 'monospace' }}>{purchase?.exchangeNumber}</b> for <b>{purchase?.client?.name}</b> ({fmt(Number(purchase?.valuedAmount))})?
        </Typography>
        <Alert severity="warning" icon={false} sx={{ mb: 1.5 }}>
          The customer's Store Credit from this purchase is taken back, the ledger entry is reversed and the metal leaves the Metal Vault.
          The record stays, marked <b>Reversed</b>. Refused if any of this metal has already left the vault. This cannot be undone.
        </Alert>
        <TextField label="Reason (required)" size="small" fullWidth autoFocus multiline minRows={2} value={reason}
          onChange={(e) => setReason(e.target.value)} inputProps={{ 'aria-label': 'Reason for deleting this purchase', maxLength: 500 }}
          helperText="Recorded in the audit trail with your name." />
        {error && <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button color="error" variant="contained" disabled={reason.trim().length < 3 || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
